import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const getMemberId = (m) => m?._id || m?.id || m?.communityId || m?.userid || "";

const getMemberMobile = (m) =>
  String(m?.mobileNumber || m?.phone || m?.mobile || "").trim();

const getMemberBlock = (m) => String(m?.blockNumber || m?.block || "").trim();

const getMemberStatus = (m) => {
  if (typeof m?.isActive === "boolean")
    return m.isActive ? "active" : "inactive";
  return (
    String(m?.status || "")
      .trim()
      .toLowerCase() || "active"
  );
};

const pickList = (data) => {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.members)) return data.members;
  if (Array.isArray(data.communities)) return data.communities;
  if (Array.isArray(data.community)) return data.community;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.list)) return data.list;
  if (Array.isArray(data.tenants)) return data.tenants;
  return [];
};

const EXECUTIVE_STATUS_LABELS = {
  active: "Active",
  tenure_completion: "Tenure Completion",
  resigned: "Resigned",
  removed: "Removed",
};

const getExecutiveStatus = (m) =>
  String(m?.executiveStatus || "")
    .trim()
    .toLowerCase() || "active";

const executiveBadgeStyle = (s) => {
  if (s === "active") {
    return { background: "rgba(34,197,94,0.18)", color: "#16a34a" };
  }
  if (s === "tenure_completion") {
    return { background: "rgba(59,130,246,0.15)", color: "#2563eb" };
  }
  if (s === "resigned") {
    return { background: "rgba(245,158,11,0.18)", color: "#d97706" };
  }
  if (s === "removed") {
    return { background: "rgba(239,68,68,0.18)", color: "#dc2626" };
  }
  return { background: "rgba(107,114,128,0.15)", color: "#4b5563" };
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const Community = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const userId = useMemo(() => localStorage.getItem("userid") || "", []);
  const role = useMemo(() => String(localStorage.getItem("role") || "").toUpperCase(),[]);
  const isAdmin = role === "ADMIN";

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [view, setView] = useState("present");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  const fetchList = useCallback(async () => {
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }

    let didIncrement = false;
    try {
      const payload = {
        action: "community.getAll",
        page,
        limit,
      };
      if (userId) {
        payload.userId = userId;
        payload.userid = userId;
      }
      payload.view = view;
      const q = String(search || "").trim();
      if (q) payload.search = q;

      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        const list = pickList(data);
        setItems(Array.isArray(list) ? list : []);
        const pag =
          data.pagination && typeof data.pagination === "object"
            ? data.pagination
            : {};
        setPagination({
          page: Number(pag.page) || page,
          limit: Number(pag.limit) || limit,
          total: Number(pag.total) || (Array.isArray(list) ? list.length : 0),
          pages: Math.max(1, Number(pag.pages) || 1),
        });
      } else {
        toast.error(data?.message || "Failed to load community members");
        setItems([]);
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong";
      toast.error(msg);
      setItems([]);
    } finally {
      if (didIncrement) loaderDecrement();
    }
  }, [API_BASE_URL, page, limit, search, status, view, userId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const applySearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const pageNum = Number(pagination.page) || page;
  const pageLimit = Number(pagination.limit) || limit;
  const totalPages = Math.max(1, Number(pagination.pages) || 1);
  const currentPage = Math.min(Math.max(1, pageNum), totalPages);

  const pageButtons = useMemo(() => {
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const pages = [];
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  }, [currentPage, totalPages]);

  const rows = Array.isArray(items) ? items : [];

  const isPast = view === "past";

  const handleView = (row) => {
    const id = getMemberId(row);
    if (!id) {
      toast.error("Missing member id");
      return;
    }
    navigate(`/community-view/${id}`);
  };

  const handleEdit = (row) => {
    const id = getMemberId(row);
    if (!id) {
      toast.error("Missing member id");
      return;
    }
    navigate(`/community-edit/${id}`);
  };

  // const openDeleteModal = (row) => {
  //   const id = getMemberId(row);
  //   if (!id) {
  //     toast.error("Missing member id");
  //     return;
  //   }
  //   setDeleteTarget(row);
  // };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    const id = getMemberId(deleteTarget);
    if (!id) return;

    setDeleting(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const payload = { action: "community.delete", communityId: id };
      if (userId) {
        payload.userId = userId;
        payload.userid = userId;
      }
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        toast.success(data?.message || "Community member deleted successfully");
        setDeleteTarget(null);
        fetchList();
      } else {
        toast.error(data?.message || "Failed to delete community member");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setDeleting(false);
    }
  };

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <h1 className="m-0 txt_lft">Executive Committee </h1>
            <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
              Manage Executive Committee members.
            </p>
          </div>
          {isAdmin && (
           <NavLink to="/community-create" className="btn btn-primary">
             Add Member
           </NavLink>
          )}
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                <div
                  className="role-table-top d-flex flex-wrap align-items-end justify-content-start"
                  style={{ gap: 12 }}
                >
                  <form
                    onSubmit={applySearch}
                    className="role-table-search-wrap"
                    style={{ flex: "1 1 220px", maxWidth: 360 }}
                  >
                    <div className="form-group mb-0">
                      <label className="form-label" style={{ fontSize: 12 }}>
                        Search
                      </label>
                      <input
                        type="text"
                        className="form-control role-table-search"
                        placeholder="Search name, email, designation…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                    </div>
                  </form>

                  <div className="form-group mb-0" style={{ width: 160 }}>
                    <label className="form-label" style={{ fontSize: 12 }}>
                      Status
                    </label>
                    <select
                      className="form-control"
                      style={{ width: "100%" }}
                      value={view}
                      onChange={(e) => {
                        setPage(1);
                        setView(e.target.value);
                      }}
                    >
                      <option value="present">Present</option>
                      <option value="past">Past</option>
                      {/* <option value="inactive">Deleted user</option> */}
                      <option value="all">All</option>
                    </select>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover role-table">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>
                          <span className="role-th">Sl No.</span>
                        </th>
                        <th>
                          <span className="role-th">Member</span>
                        </th>
                        <th>
                          <span className="role-th">Position</span>
                        </th>
                        <th>
                          <span className="role-th">Email</span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">Mobile</span>
                        </th>
                        <th style={{ width: 80 }}>
                          <span className="role-th">Block</span>
                        </th>
                        <th style={{ width: 90 }}>
                          <span className="role-th">Flat</span>
                        </th>
                        {isPast && (
                          <>
                            <th style={{ width: 110 }}>
                              <span className="role-th">From Date</span>
                            </th>
                            <th style={{ width: 110 }}>
                              <span className="role-th">To Date</span>
                            </th>
                          </>
                        )}
                        <th style={{ width: 150 }}>
                          <span className="role-th">Executive Status</span>
                        </th>
                       {isAdmin && (
                         <th style={{ width: 120 }}>
                           <span className="role-th">Action</span>
                         </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length ? (
                        rows.map((row, idx) => {
                          const id = getMemberId(row);
                          const name = row?.name || "—";
                          const memberStatus = getMemberStatus(row);

                          return (
                            <tr key={String(id || `row-${idx}`)}>
                              <td>{(pageNum - 1) * pageLimit + idx + 1}</td>
                              <td>{name}</td>
                              <td>{row?.designation || "—"}</td>
                              <td>{row?.email || "—"}</td>
                              <td>{getMemberMobile(row) || "—"}</td>
                              <td>{getMemberBlock(row) || "—"}</td>
                              <td>{row?.flatNumber || "—"}</td>
                              {isPast && (
                                <>
                                  <td>
                                    {formatDate(
                                      row?.fromDate || row?.createdAt,
                                    )}
                                  </td>
                                  <td>{formatDate(row?.toDate)}</td>
                                </>
                              )}
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    padding: "0.35rem 0.65rem",
                                    borderRadius: 999,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    ...executiveBadgeStyle(
                                      getExecutiveStatus(row),
                                    ),
                                  }}
                                >
                                  {(
                                    EXECUTIVE_STATUS_LABELS[
                                      getExecutiveStatus(row)
                                    ] || getExecutiveStatus(row)
                                  ).toUpperCase()}
                                </span>
                              </td>
                              {isAdmin && (
                              <td>
                                <div className="d-flex" style={{ gap: 8 }}>
                                  <button
                                    type="button"
                                    className="role-action-btn role-action-view"
                                    title="View"
                                    onClick={() => handleView(row)}
                                  >
                                    <i
                                      className="fa-solid fa-eye"
                                      aria-hidden="true"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className="role-action-btn role-action-edit"
                                    title="Edit"
                                    onClick={() => handleEdit(row)}
                                  >
                                    <i
                                      className="fa-solid fa-pen-to-square"
                                      aria-hidden="true"
                                    />
                                  </button>
                                  {/* {memberStatus !== "inactive" && (
                                    <button
                                      type="button"
                                      className="role-action-btn role-action-delete"
                                      title="Delete"
                                      onClick={() => openDeleteModal(row)}
                                    >
                                      <i
                                        className="fa-solid fa-trash"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  )} */}
                                </div>
                              </td>
                              )}
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={isPast ? 12 : 10}
                            className="text-center text-muted"
                          >
                            {search
                              ? "No Executive Committee  members found"
                              : "No Executive Committee  members"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div
                  className="role-table-footer d-flex justify-content-between align-items-center flex-wrap"
                  style={{ gap: 10 }}
                >
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: 10 }}
                  >
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      Show:
                    </span>
                    <select
                      className="form-control"
                      style={{ width: 90 }}
                      value={limit}
                      onChange={(e) => {
                        setPage(1);
                        setLimit(Number(e.target.value));
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      Total: {pagination.total || 0}
                    </span>
                  </div>

                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage <= 1}
                      onClick={() => setPage(1)}
                    >
                      &laquo;
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      &lsaquo;
                    </button>
                    {pageButtons.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`btn ${p === currentPage ? "btn-primary" : "btn-light"}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      &rsaquo;
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage(totalPages)}
                    >
                      &raquo;
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {deleteTarget ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={closeDeleteModal}
        >
          <div
            className="card app-modal-card"
            style={{
              width: "min(480px, 96vw)",
              maxHeight: "90vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header app-modal-header">
              <div className="app-modal-title">Delete member</div>
              <button
                type="button"
                className="app-modal-close"
                onClick={closeDeleteModal}
                disabled={deleting}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="card-body">
              Are you sure you want to delete{" "}
              <strong>{deleteTarget?.name || "this member"}</strong>?
            </div>

            <div
              className="card-footer d-flex justify-content-end"
              style={{ gap: 10 }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Community;
